import React from 'react';
import { Box, Text, Flex, AccordionListItem } from '@opendoor/bricks/core';
import { MarginProps } from '@opendoor/bricks/system/theme';
import { useOfferContext } from 'sellDirect/OfferContext';
import { useObservability } from 'shared/helpers/observability';
import { useExperimentationContext } from 'sellDirect/ExperimentationContext';
import { CXA_EMAIL_NEW } from 'shared/helpers/globals';

const agentSsvaQuestionData = (isCepEmailUpdatesEnabled: boolean) => {
  return {
    info: [
      {
        question: 'What are you looking for in my client’s home?',
        answer: (
          <Text>
            We use the video walkthrough to capture the home’s layout and key features. Your client
            doesn’t need to clean, stage, or prepare the home in any special way. We use the
            exterior assessment and quick interior walkthrough to ensure that major areas are safe,
            functional and in good repair.
          </Text>
        ),
        trackingId: 'agent-what-looking-for-clients-home',
      },
      {
        question: 'Do I need to be present for the in-person assessment?',
        answer: (
          <Text>
            Either you or your seller needs to provide access to the backyard and inside of the
            home. If no one can be home, please contact your Opendoor expert. The total in-person
            assessment should not be longer than 30 minutes.
          </Text>
        ),
        trackingId: 'agent-present-for-in-person-assessment',
      },
      {
        question: 'When will I receive a finalized offer?',
        answer: (
          <Text>
            Once you’ve completed all the tasks associated with the virtual walkthrough and
            in-person assessments, you’ll receive your client’s finalized offer in a few days.
          </Text>
        ),
        trackingId: 'agent-when-finalized-offer',
      },
      {
        question: 'Is this offer final?',
        answer: (
          <>
            <Text>
              Not yet. Before we can prepare the final offer price, we need to see the home. Upload
              photos and videos of the home and shortly after, an Opendoor team member will complete
              an exterior and interior walkthrough of the property.
            </Text>
            {'\n'}
            <Text>
              Shortly after reviewing all assessment information, you’ll get a final offer price
              that details the service charge and estimated closing costs.
            </Text>
          </>
        ),
        trackingId: 'agent-is-offer-final',
      },
      {
        question: 'What if this isn’t the number I was expecting?',
        answer: (
          <Text>
            If you think we missed something, let us know and we can reassess. Call us at
            844-480-0180 or email us at{' '}
            {isCepEmailUpdatesEnabled ? CXA_EMAIL_NEW : 'contact@opendoor.com'}. We can incorporate
            your feedback into the final offer price.
          </Text>
        ),
        trackingId: 'agent-offer-mismatch-expectation',
      },
      {
        question: 'What fees will I have to pay?',
        answer: (
          <>
            <Text>
              Our goal is to help you understand your client’s costs before making any decisions.
              With Opendoor, your client pays a service charge, repair costs, and closing costs like
              what they’d pay in any real estate transaction.
            </Text>
            {'\n'}
            <Text>To learn more about our pricing, visit opendoor.com/pricing.</Text>
          </>
        ),
        trackingId: 'agent-fees',
      },
      {
        question: 'Will the offer expire?',
        answer: (
          <Text>
            Your client’s Opendoor offer expires after 7 days. If you submit photos and videos, we
            will extend their offer expiration to 14 days. You can request a new offer for your
            client from your dashboard, so it’s always up-to-date based on market trends.
          </Text>
        ),
        trackingId: 'agent-offer-expire',
      },
    ],
  };
};

/** Common questions and answers to display in the SSVA page */
const SSVA_QUESTION_DATA = {
  info: [
    {
      question: 'Why should I show you my home?',
      answer: (
        <Text>When we see your home, we can account for its features to make our best offer.</Text>
      ),
      trackingId: 'why-show-home',
    },
    {
      question: 'What are you looking for when I show you my home?',
      answer: (
        <Text>
          We’re looking at your home’s layout and features. There's no need to do anything special
          to prepare--you don't even have to clean!
        </Text>
      ),
      trackingId: 'opendoor-looking-for',
    },
    {
      question: 'Which is the best option?',
      answer: (
        <>
          <Text>
            It’s really a matter of preference! If you prefer to show us your home live on a video
            call, scheduling is probably the right option. If you’d rather go at your own pace then
            the self-guided experience could be a better fit.
          </Text>
          {'\n'}
          <Text>
            Either way, our pricing experts will review your information and prepare a competitive
            offer for your home.
          </Text>
        </>
      ),
      trackingId: 'best-va-option',
    },
    {
      question: 'What happens after I show you my home?',
      answer: (
        <>
          <Text>
            Once you’ve completed the walkthrough, we’ll find time for a home assessment. Once
            that's complete, we’ll have everything we need to prepare your final offer.
          </Text>
          {'\n'}
          <Text>
            We’ll send an email a few days later with your final offer amount, service charge and
            any expected repair costs.
          </Text>
        </>
      ),
      trackingId: 'after-home-va',
    },
    {
      question: 'When can I see my final offer?',
      answer: (
        <Text>
          Once you’ve completed the walkthrough, we’ll find time for a home assessment. A few days
          after that’s complete, we’ll send an email with your final offer amount, service charge
          and any expected repair costs.
        </Text>
      ),
      trackingId: 'seeing-final-offer',
    },
  ],
};

/** Shows a list of accordions containing common questions with answers about the SSVA experience */
export const SSVACommonQuestions: React.FC<MarginProps> = (marginProps) => {
  const { shouldSeeAgentExperience } = useOfferContext();
  const { trackUserEvent } = useObservability();
  const {
    assertions: { isCepEmailUpdatesEnabled },
  } = useExperimentationContext();
  const questionData = shouldSeeAgentExperience
    ? agentSsvaQuestionData(isCepEmailUpdatesEnabled)
    : SSVA_QUESTION_DATA;

  return (
    <Box {...marginProps}>
      <Text as="h2" fontWeight="bold" mb="28px" fontSize="s2" ml="3">
        Common questions
      </Text>
      <Flex flexDir="column" gridGap={7}>
        {questionData.info.map(({ question, answer, trackingId }) => (
          <AccordionListItem
            key={question}
            panelId={trackingId}
            title={<Text>{question}</Text>}
            noPadding
            variant="secondary"
            onOpen={() =>
              trackUserEvent({
                buttonClickEvent: {
                  targetName: `cake-ssva-common-questions-noo-experiment-${trackingId}`,
                },
              })
            }
          >
            {answer}
          </AccordionListItem>
        ))}
      </Flex>
    </Box>
  );
};
